import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw, Link2, CheckCircle2, XCircle, ShieldAlert } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function DuplicateAuditModule({ token }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(() => {
    axios.get(`${API}/finance/duplicate-audit`, auth).then((r) => setData(r.data))
      .catch(() => toast.error("Could not load the duplicate audit."));
    // eslint-disable-next-line
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const decide = async (action, row) => {
    setBusy(row.ref);
    try {
      await axios.post(`${API}/finance/duplicate-audit/${action}`, { internal_ref: row.ref, processor_ref: row.linked_to }, auth);
      toast.success(action === "confirm" ? "Confirmed — this record no longer counts toward revenue." : "Dismissed — both records keep counting as separate payments.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save decision."); }
    finally { setBusy(""); }
  };

  const decideStorm = async (action, storm) => {
    setBusy(storm.canonical_ref);
    try {
      await Promise.all(storm.members.map((ref) =>
        axios.post(`${API}/finance/duplicate-audit/${action}`, { internal_ref: ref, processor_ref: storm.canonical_ref }, auth)));
      toast.success(action === "confirm"
        ? `Confirmed — all ${storm.count} rows no longer count toward revenue.`
        : `Dismissed — all ${storm.count} rows keep counting as separate payments.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save decision."); }
    finally { setBusy(""); }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#F2B705]" /></div>;

  const cards = [
    ["Old Reported Revenue", data.currently_reported_revenue, "text-white"],
    ["Correct Revenue (deduplicated)", data.correct_deduplicated_revenue, "text-green-400"],
    ["Total Overstatement Removed", data.difference, data.difference > 0 ? "text-red-400" : "text-white/50"],
    ["Duplicate Records Linked", data.duplicate_records_found, "text-[#F2B705]"],
  ];

  return (
    <div className="space-y-5" data-testid="duplicate-audit-module">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><Link2 size={18} className="text-[#F2B705]" /> Duplicate Payment Audit</h3>
          <p className="text-white/45 text-xs mt-0.5">{data.rule}</p>
        </div>
        <button data-testid="dup-audit-refresh" onClick={load} className="flex items-center gap-1.5 text-xs border border-white/15 px-3 py-2 rounded hover:bg-white/10 whitespace-nowrap shrink-0"><RefreshCw size={13} /> Re-run</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map(([label, val, cls]) => (
          <div key={label} className="bg-[#0C0C0C] border border-white/10 rounded-xl p-4" data-testid={`dup-card-${label.split(" ")[0].toLowerCase()}`}>
            <div className="text-white/45 text-[11px] uppercase tracking-wider">{label}</div>
            <div className={`font-display font-black text-xl mt-1 ${cls}`}>{typeof val === "number" && label !== "Duplicate Records Linked" ? money(val) : val}</div>
          </div>
        ))}
      </div>

      <div>
        <h4 className="text-sm font-bold text-white mb-2">Confirmed duplicates — excluded from totals ({data.duplicates.length})</h4>
        {data.duplicates.length === 0 ? <p className="text-white/35 text-sm">None found. One real dollar = one dollar counted.</p> : (
          <div className="space-y-2">
            {data.duplicates.map((d) => (
              <div key={d.ref} data-testid={`dup-row-${d.ref}`} className="rounded-lg border border-white/10 bg-[#0A0A0A] px-4 py-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-white/85">{d.date} · <b>{d.customer}</b>{d.vehicle ? ` · ${d.vehicle}` : ""}</span>
                  <span className="font-bold text-[#F2B705]">{money(d.amount)}</span>
                </div>
                <div className="mt-1.5 text-xs space-y-0.5">
                  <p className="text-green-400 flex items-center gap-1"><CheckCircle2 size={11} /> Processor payment ({d.linked_to}) → COUNTED</p>
                  <p className="text-white/50 flex items-center gap-1"><Link2 size={11} /> Internal {d.type} ({d.ref}) → LINKED / NOT COUNTED</p>
                  <p className="text-white/35">Actual Money In → counted once: {money(d.amount)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h4 className="text-sm font-bold text-amber-300 mb-2 flex items-center gap-1.5"><ShieldAlert size={14} /> Possible duplicate storms — still counted until you decide ({data.possible_duplicate_storms?.length || 0} storm{(data.possible_duplicate_storms?.length || 0) === 1 ? "" : "s"}, {data.possible_duplicates_review.length} row{data.possible_duplicates_review.length === 1 ? "" : "s"} total)</h4>
        {(!data.possible_duplicate_storms || data.possible_duplicate_storms.length === 0) ? <p className="text-white/35 text-sm">Nothing awaiting review.</p> : (
          <div className="space-y-2">
            {data.possible_duplicate_storms.map((s) => (
              <div key={s.canonical_ref} data-testid={`storm-row-${s.canonical_ref}`} className="rounded-lg border border-amber-400/25 bg-amber-400/[0.04] px-4 py-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-white/85">{s.date} · <b>{s.customer}</b>{s.vehicle ? ` · ${s.vehicle}` : ""} · {s.count}x {money(s.amount_each)}</span>
                  <span className="font-bold text-amber-300">{money(s.total_amount_still_counted)} total</span>
                </div>
                <p className="text-white/45 text-xs mt-1">{s.note}</p>
                <div className="flex gap-2 mt-2">
                  <button data-testid={`storm-confirm-${s.canonical_ref}`} disabled={busy === s.canonical_ref} onClick={() => decideStorm("confirm", s)}
                    className="text-xs font-bold bg-[#F2B705] text-black px-3 py-1.5 rounded hover:bg-white disabled:opacity-50 flex items-center gap-1">
                    <CheckCircle2 size={12} /> Confirm all {s.count} — stop counting duplicates
                  </button>
                  <button data-testid={`storm-dismiss-${s.canonical_ref}`} disabled={busy === s.canonical_ref} onClick={() => decideStorm("dismiss", s)}
                    className="text-xs font-semibold border border-white/20 text-white/70 px-3 py-1.5 rounded hover:bg-white/10 disabled:opacity-50 flex items-center gap-1">
                    <XCircle size={12} /> Dismiss — all {s.count} are separate payments
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
